import React, { memo, Fragment, useState } from 'react'
import { useTheme, withStyles } from '@material-ui/core/styles'
import FormControl from '@material-ui/core/FormControl'
import IconButton from '@material-ui/core/IconButton'
import Input from '@material-ui/core/Input'
import InputAdornment from '@material-ui/core/InputAdornment'
import InputLabel from '@material-ui/core/InputLabel'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import MoreHorizOutlined from '@material-ui/icons/MoreHorizOutlined'
import Paper from '@material-ui/core/Paper'
import Popper from '@material-ui/core/Popper'
import Typography from '@material-ui/core/Typography'
import moment from 'moment'
import RepeatDialog from './RepeatDialog'
import clsx from 'clsx'
import RepeatPattern from '../../data/util/repeat/RepeatPattern'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import LOG from '../../logging/Logger'
import { MAX_LENGTH_REPEAT } from '../../data/limits'

// use withStyles rather than makeStyles as don't know how to let parent override styles otherwise
const styles = theme => ({
  popper: {
    zIndex: theme.zIndex.tooltip
  },
  margin: {},
  fullWidth: {},
  paper: {
    padding: theme.spacing(3)
  }
})

const MAX_DATES = 3

export default withStyles(styles)(
  memo(({ classes, onChangeRepeat, repeat }) => {
    LOG.debug('Render: RepeatInput')

    const theme = useTheme()

    const [openRepeatSetup, setOpenRepeatSetup] = useState(false)
    const [showHint, setShowHint] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)

    const mobileSize = useMediaQuery(theme.breakpoints.down('xs'), {
      noSsr: true
    })

    const validateRepeat = () => {
      if (!repeat) {
        return true
      }

      return new RepeatPattern(repeat).isValidRepeatPattern()
    }

    const cleanedRepeat = () => {
      if (!repeat || repeat.length === 0) {
        return null
      }
      return new RepeatPattern(repeat).toString()
    }

    const nextDates = ruleText => {
      return new RepeatPattern(ruleText).nextDates()
    }

    const handleClick = event => {
      setAnchorEl(event.currentTarget)
    }

    const handleAutoComplete = (event, value) => {
      onChangeRepeat(value)
      setShowHint(false)
      event.preventDefault()
    }

    const handleChangeRepeat = event => {
      onChangeRepeat(event.target.value)
      setShowHint(true)
    }

    const handleKeyPress = (event, value) => {
      if (event.repeat) {
        return
      }
      switch (event.keyCode) {
        case 9:
        case 13:
          handleAutoComplete(event, value)
          break
        default:
          break
      }
    }

    const cleanedRepeatText = cleanedRepeat()
    const nextDatesArray = nextDates(cleanedRepeatText)

    return (
      <Fragment>
        <Popper
          className={classes.popper}
          open={
            showHint &&
            new RepeatPattern(cleanedRepeatText).isValidRepeatPattern()
          }
          anchorEl={anchorEl}
          placement='top-start'
          onClick={e => handleAutoComplete(e, cleanedRepeatText)}
          modifiers={{
            flip: {
              enabled: !mobileSize
            }
          }}
        >
          <Paper className={classes.paper} elevation={3}>
            <Typography variant='h6' gutterBottom>
              {cleanedRepeatText}
            </Typography>
            <em>
              {nextDatesArray && nextDatesArray.length > 1 && (
                <Typography variant='body1'>
                  Next {Math.min(nextDatesArray.length, MAX_DATES)} occurrences:
                </Typography>
              )}
              {nextDatesArray && nextDatesArray.length === 1 && (
                <Typography variant='body1'>Repeat on:</Typography>
              )}
            </em>
            <List>
              {nextDatesArray &&
                nextDatesArray.slice(0, MAX_DATES).map(date => (
                  <ListItem key={date}>
                    <ListItemText
                      primary={moment(date).format('dddd, MMMM Do YYYY')}
                    />
                  </ListItem>
                ))}
            </List>
          </Paper>
        </Popper>
        <FormControl className={clsx(classes.margin, classes.fullWidth)}>
          <InputLabel htmlFor='standard-adornment-repeat' shrink={true}>
            Repeat
          </InputLabel>
          <Input
            value={repeat || ''}
            error={!validateRepeat(repeat)}
            placeholder='e.g. Every week on Monday, Wednesday for 6 times'
            onClick={handleClick}
            onChange={handleChangeRepeat}
            onKeyDown={e => handleKeyPress(e, cleanedRepeatText)}
            inputProps={{
              'aria-label': 'repeat',
              'data-lpignore': 'true',
              maxLength: MAX_LENGTH_REPEAT
            }}
            endAdornment={
              false && (
                <InputAdornment position='end'>
                  <IconButton
                    className={classes.endAdornment}
                    aria-label='show repeat setup'
                    onClick={() => {
                      setShowHint(false)
                      setOpenRepeatSetup(true)
                    }}
                  >
                    <MoreHorizOutlined />
                  </IconButton>
                </InputAdornment>
              )
            }
          />
        </FormControl>
        <RepeatDialog
          open={openRepeatSetup}
          onSave={repeatString => {
            onChangeRepeat(repeatString)
            setOpenRepeatSetup(false)
          }}
          onClose={() => {
            setOpenRepeatSetup(false)
          }}
        />
      </Fragment>
    )
  })
)
